import { useMemo, useState, memo } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatRego, formatAUMobile } from '@/lib/formatters';
import { withSessionHash } from '@/lib/authFetch';
import { toast } from 'sonner';
import { 
  Car, 
  User, 
  Phone,
  Wrench,
  Clock,
  ClipboardCheck,
  FileCheck,
  Key,
  CalendarClock,
} from 'lucide-react';
import JobCardMenu from './JobCardMenu';
import JobCardLabourLive from './JobCardLabourLive';
import JobCardPerMechanicHours from './JobCardPerMechanicHours';
import { PRIORITY_CONFIG, SERVICE_TYPE_LABELS } from './JobCardDetailModal';
import { formatMelbourneDate, formatDeliveryDueLabel } from '@/lib/melbourneTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* iter202 — roles that may jump from the tile to the linked invoice / its
   PDF (owner asked for one-click PDF access straight from the JC tile). */
const INVOICE_LINK_ROLES = ['super_admin', 'admin', 'service_manager'];

const openInvoicePdf = async (e, invoiceId) => {
  e.preventDefault();
  e.stopPropagation();
  try {
    const r = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}/pdf`, { credentials: 'include' });
    if (!r.ok) throw new Error();
    const blob = await r.blob();
    window.open(URL.createObjectURL(blob), '_blank');
  } catch (_e) {
    toast.error("Couldn't open the invoice PDF — try again");
  }
};

/* Inline Parts-Ordered pill — mirrors the wallet tile's OP/PO toggle so
   SM can flag parts ordered from EITHER view. Same endpoint + same
   visual (red OP pulsing / emerald PO static).
   iter138.3 — Mechanic-side fix: previously the pill rendered for
   SM/FM only, so a mechanic on the Status board couldn't see whether
   their parts had been ordered. Now we render for EVERY viewer; only
   SM/FM/Admin/SA can click to toggle (mechanic sees it as a read-only
   pill). When parts haven't been ordered (OP), we don't show anything
   to the mechanic — only the green PO confirmation matters to them. */
function PartsPill({ card, canManage, onUpdate }) {
  const [busy, setBusy] = useState(false);
  // Only meaningful for active work-states (booking has no work yet;
  // sm_review/completed are post-work).
  const eligible = ['pickup', 'in_progress', 'break', 'waiting_for_parts', 'blocked'].includes(card.status);
  if (!eligible) return null;
  // Mechanic view — only show the GREEN PO chip (good news). Hide the
  // red "Order Parts" until SM toggles it.
  if (!canManage) {
    if (!card.parts_ordered) return null;
    return (
      <span
        data-testid={`parts-pill-ro-${card.job_card_id}`}
        title={`Parts ordered${card.parts_ordered_at ? ` ${new Date(card.parts_ordered_at).toLocaleDateString('en-AU')}` : ''}`}
        className="inline-flex items-center justify-center text-[10px] font-extrabold uppercase w-7 h-5 rounded border leading-none bg-emerald-500 text-white border-emerald-600"
      >
        PO
      </span>
    );
  }
  const toggle = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/job-cards/${card.job_card_id}/toggle-parts-ordered`,
        { method: 'POST', credentials: 'include' },
      );
      if (r.ok) {
        const d = await r.json();
        toast.success(d.parts_ordered ? 'Parts marked ORDERED' : 'Parts flag cleared');
        if (onUpdate) onUpdate();
      } else {
        toast.error('Could not update parts status');
      }
    } catch (_e) {
      toast.error('Network error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      disabled={busy}
      onClick={toggle}
      onMouseDown={(e) => e.stopPropagation()}
      data-testid={`parts-pill-${card.job_card_id}`}
      title={card.parts_ordered ? 'Parts Ordered — click to clear' : 'Click to flag parts as ordered'}
      className={`
        inline-flex items-center justify-center text-[10px] font-extrabold uppercase
        w-7 h-5 rounded border transition-colors leading-none
        ${busy ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
        ${card.parts_ordered
          ? 'bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600'
          : 'bg-rose-500 text-white border-rose-600 hover:bg-rose-600 animate-pulse'}
      `}
    >
      {card.parts_ordered ? 'PO' : 'OP'}
    </button>
  );
}

/* iter164 — Ready-to-Quote / Invoice-Created pill. Sits next to the
   rego (same visual pattern as PO/OP). Mechanic sets `ready_to_quote`
   from inside the JC modal after completing inspection + findings; the
   pill turns ORANGE for SM to see. When SM creates an invoice from the
   JC, backend clears the flag and sets `invoice_id` → the pill flips
   to EMERALD "Invoice Created". Read-only from the tile — clicks on
   the tile open the JC modal where mechanics can toggle the state. */
function ReadyToQuotePill({ card }) {
  if (card.invoice_id) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider text-emerald-900 bg-emerald-100 border border-emerald-400 rounded px-1.5 py-0.5 leading-none"
        data-testid={`ready-to-quote-invoice-badge-${card.job_card_id}`}
        title="Invoice created for this JC — click the tile to view or open the linked invoice"
      >
        <FileCheck className="h-2.5 w-2.5" />
        Invoice Created
      </span>
    );
  }
  if (!card.ready_to_quote) return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider text-orange-900 bg-orange-200 border border-orange-400 rounded px-1.5 py-0.5 leading-none"
      data-testid={`ready-to-quote-badge-${card.job_card_id}`}
      title="Mechanic marked this JC ready to quote — inspection & findings are complete. Click the tile to review and create an invoice."
    >
      <FileCheck className="h-2.5 w-2.5" />
      Ready to Quote
    </span>
  );
}


/* iter138.3 — Priority-rank badge. SM/FM drag-to-reorder in the wallet
   view sets `priority_rank` (1 = top of the lane). Mechanic needs to
   see this on every tile so they know which JC to pick up next — the
   only thing previously surfaced was the priority LABEL (low/medium/
   high/urgent) which doesn't communicate ordering. Compact, dark pill
   so it stands out from the rest of the badges. Null rank → not shown. */
function RankBadge({ card, size = 'sm' }) {
  const rank = card.priority_rank;
  if (!Number.isFinite(rank) || rank < 1) return null;
  const cls = size === 'lg'
    ? 'text-xs font-extrabold px-2 py-0.5 rounded-md'
    : 'text-[10px] font-extrabold px-1.5 py-0.5 rounded';
  return (
    <span
      data-testid={`jc-rank-badge-${card.job_card_id}`}
      title={`Priority rank #${rank} — set by SM/FM. Lower number = higher priority.`}
      className={`bg-slate-900 text-white tracking-tight tabular-nums leading-none ${cls}`}
    >
      #{rank}
    </span>
  );
}

/* iter131 Phase 2 — "On Break" pulsing pill. Surfaces on JC tiles when the
   assigned mechanic is currently on break (workday.state='on_break'). The
   JC's `on_break` flag is set by /mechanic/break-start and cleared by
   /mechanic/break-end (or /mechanic/clock-off). Visible to ALL viewers so
   SM/FM/SA know at a glance who's paused. */
function OnBreakPill({ card }) {
  if (!card.on_break) return null;
  return (
    <span
      data-testid={`on-break-pill-${card.job_card_id}`}
      title="Mechanic is on break — JC clock is paused"
      className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase text-amber-900 bg-amber-200 border border-amber-400 rounded px-1.5 py-0.5 leading-none animate-pulse"
    >
      ☕ ON BREAK
    </span>
  );
}

function JobCardItemInner({ 
  card, 
  size, 
  canManage, 
  user,
  mechanics,
  onFetchMechanics,
  onUpdateDeliveryDue, 
  onAssignMechanic, 
  onDelete,
  onCreateInvoice,
  onUpdate,
  isDragging,
  duplicateFlags,
}) {
  const priorityConfig = PRIORITY_CONFIG[card.priority] || PRIORITY_CONFIG.medium;   
  const hasChecklist = card.quality_checklist && Object.values(card.quality_checklist).some(v => v);
  const isEscalated = !!card.escalated;

  // Sum of `quoted_labour_hours` across all tasks. Shown as a small
  // ⏱ badge so SM can eyeball *"how much work is on this JC?"* at a
  // glance — pairs with the page-level capacity banner.
  const totalHours = useMemo(() => {
    if (!card?.line_items) return 0;
    return card.line_items.reduce(
      (s, li) => s + (Number(li.quoted_labour_hours) || 0),
      0,
    );
  }, [card?.line_items]);
  // React Compiler memoizes this automatically — no manual useMemo needed.
  const totalHoursLabel = totalHours > 0
    ? (Number.isInteger(totalHours) ? `${totalHours}h` : `${totalHours.toFixed(1)}h`)
    : '';

  // iter138.9v — Delivery Due display. SM/FM sets this on the 3-dot
  // menu (replaces the old Low/Med/Urgent priority picker). The value
  // is stored as a NAIVE local string (no TZ) so the SM's intent is
  // preserved regardless of browser TZ — see formatDeliveryDueLabel.
  const deliveryDueLabel = formatDeliveryDueLabel(card.delivery_due_at);
  
  const formatBookedDate = (dateStr) => {
    if (!dateStr) return '';
    // Schedule DATE matters (when the customer is dropping off), schedule
    // time does not. Render eg. "Tue, 17 Feb".
    return formatMelbourneDate(dateStr, { weekday: 'short', day: '2-digit', month: 'short' });
  };

  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  if (size === 'large') {
    return (
      <Card 
        className={`p-4 bg-white border shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
          isDragging ? 'shadow-lg ring-2 ring-blue-400' : ''
        } ${isEscalated ? 'ring-2 ring-amber-400 border-amber-300 bg-amber-50/40' : ''}`}
        data-testid={`job-card-${card.job_card_id}`}
      >
        {isEscalated && (
          <div className="mb-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-200 px-2 py-0.5 rounded-full" data-testid="escalation-badge">
            ⚠ Escalated to SM
          </div>
        )}
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="font-bold text-slate-900 text-xl tracking-wide">{formatRego(card.vehicle_rego) || 'NO REGO'}</span>
              <ReadyToQuotePill card={card} />
              <RankBadge card={card} size="lg" />
              <PartsPill card={card} canManage={canManage} onUpdate={onUpdate} />
              <OnBreakPill card={card} />
              {hasChecklist && (
                <ClipboardCheck className="h-4 w-4 text-green-500" />
              )}
              {totalHoursLabel && (
                <span
                  className="text-[10px] font-semibold uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1"
                  data-testid={`jc-hours-badge-${card.job_card_id}`}
                  title="Total quoted labour hours across all tasks on this job card"
                >
                  <Clock className="h-2.5 w-2.5" />
                  {totalHoursLabel}
                </span>
              )}
              {/* iter138.9v — DD By (Delivery Due) sits NEXT TO quoted
                  hours in the Status view, replacing the priority Badge
                  above. SM/FM picks this on the 3-dot menu. */}
              {deliveryDueLabel && (
                <span
                  className="text-[10px] font-semibold uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5 inline-flex items-center gap-1"
                  data-testid={`jc-delivery-due-${card.job_card_id}`}
                  title="Delivery due — when the car must leave the workshop. Set by SM/FM on the 3-dot menu."
                >
                  <CalendarClock className="h-2.5 w-2.5" />
                  DD By {deliveryDueLabel}
                </span>
              )}
              {duplicateFlags && duplicateFlags.length > 0 && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wide bg-rose-50 text-rose-700 border border-rose-300 rounded px-1.5 py-0.5 inline-flex items-center gap-1 animate-pulse"
                  data-testid={`jc-duplicate-badge-${card.job_card_id}`}
                  title={`Possible duplicate of another JC today: ${duplicateFlags.map((d) => `${d.reason.replace('_', ' ')} (${d.key})`).join(' · ')}`}
                >
                  ⚠ Dup
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {card.assigned_mechanic_name ? (
                <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded">
                  <Wrench className="h-3 w-3 inline mr-1" />
                  {card.assigned_mechanic_name}
                </span>
              ) : (
                <span className="text-xs text-slate-400">Unassigned</span>
              )}
              <Badge variant="outline" className="text-xs">
                {SERVICE_TYPE_LABELS[card.service_type] || card.service_type}
              </Badge>
            </div>
          </div>
          
          {canManage && (
            <div onClick={stopPropagation}>
              <JobCardMenu 
                card={card}
                user={user}
                mechanics={mechanics}
                onFetchMechanics={onFetchMechanics}
                onUpdateDeliveryDue={onUpdateDeliveryDue}
                onAssignMechanic={onAssignMechanic}
                onDelete={onDelete}
                onCreateInvoice={onCreateInvoice}
              />
            </div>
          )}
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-xs text-blue-600">
            <span>{card.job_card_number}</span>
            {(card.vehicle_make || card.vehicle_model) && (
              <span className="text-slate-400">•</span>
            )}
            {(card.vehicle_make || card.vehicle_model) && (
              <span className="text-slate-500">{[card.vehicle_make, card.vehicle_model].filter(Boolean).join(' ')}</span>
            )}
          </div>
          
          <div className="flex items-center gap-2 text-slate-700">
            <User className="h-4 w-4 text-slate-400" />
            <span className="font-medium">{card.customer_name || 'Unknown Customer'}</span>
          </div>
          
          {card.customer_phone && (
            <div className="flex items-center gap-2 text-slate-600">
              <Phone className="h-4 w-4 text-slate-400" />
              <span>{formatAUMobile(card.customer_phone)}</span>
            </div>
          )}
          
          <div className="flex items-center gap-2 text-slate-600">
            <Clock className="h-4 w-4 text-slate-400" />
            <span data-testid={`jc-booked-date-${card.job_card_id}`}>{formatBookedDate(card.booked_datetime) || 'No booking'}</span>
          </div>
          {/* Loan car requested — cyan pill so SM spots it before
              handing keys over. */}
          {card.loan_car_requested && (
            <div
              className="inline-flex items-center gap-1.5 text-xs font-medium text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-2 py-1 w-fit"
              data-testid={`jc-loan-car-pill-${card.job_card_id}`}
              title="Customer requested a loan car"
            >
              <Key className="h-3.5 w-3.5" />
              Loan car requested
            </div>
          )}
          {/* Invoice-created green tick — visible only to SM + Super
              Admin (iter138.9ag — FM and mechanics must not see this
              link on the JC tile per user request). */}
          {card.invoice_id && INVOICE_LINK_ROLES.includes(user?.role) && (
            <div className="flex items-center gap-1.5" onClick={stopPropagation}>
              <a
                href={withSessionHash(`/invoices/${card.invoice_id}`)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={stopPropagation}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1 hover:bg-emerald-100 w-fit"
                data-testid={`jc-invoice-created-${card.job_card_id}`}
                title="Open linked invoice in a new tab"
              >
                <FileCheck className="h-3.5 w-3.5" />
                Invoice created
              </a>
              <button
                type="button"
                onClick={(e) => openInvoicePdf(e, card.invoice_id)}
                className="inline-flex items-center gap-1 text-xs font-bold text-white bg-emerald-600 border border-emerald-700 rounded-full px-2 py-1 hover:bg-emerald-700"
                data-testid={`jc-invoice-pdf-${card.job_card_id}`}
                title="Open the invoice PDF in a new tab"
              >
                PDF
              </button>
            </div>
          )}
          {/* Dedicated productivity row — labour pill + over/under signal. */}
          <div className="pt-1 border-t border-slate-100" data-testid={`jc-productivity-row-${card.job_card_id}`}>
            <JobCardLabourLive card={card} />
            {/* iter138.9y — Per-mechanic breakdown (Option B): when 2+
                mechanics have contributed to this JC, show each one's
                clocked hours so the team can see who produced what. */}
            <JobCardPerMechanicHours card={card} mechanics={mechanics} />
          </div>
        </div>
      </Card>
    );
  }
  
  if (size === 'medium') {
    return (
      <Card 
        className={`p-3 bg-white border shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
          isDragging ? 'shadow-lg ring-2 ring-blue-400' : ''
        }`}
        data-testid={`job-card-${card.job_card_id}`}
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-slate-900 text-lg tracking-wide">{formatRego(card.vehicle_rego) || 'NO REGO'}</span>
              <ReadyToQuotePill card={card} />
              <RankBadge card={card} size="sm" />
              <PartsPill card={card} canManage={canManage} onUpdate={onUpdate} />
              <OnBreakPill card={card} />
              {hasChecklist && <ClipboardCheck className="h-3 w-3 text-green-500" />}
              {totalHoursLabel && (
                <span
                  className="text-[9px] font-semibold uppercase bg-blue-50 text-blue-700 border border-blue-200 rounded px-1 py-0.5 inline-flex items-center gap-0.5"
                  data-testid={`jc-hours-badge-${card.job_card_id}`}
                  title="Total quoted labour hours across all tasks on this job card"
                >
                  <Clock className="h-2 w-2" />
                  {totalHoursLabel}
                </span>
              )}
              {deliveryDueLabel && (
                <span
                  className="text-[9px] font-semibold uppercase bg-amber-50 text-amber-800 border border-amber-300 rounded px-1 py-0.5 inline-flex items-center gap-0.5"
                  data-testid={`jc-delivery-due-${card.job_card_id}`}
                  title="Delivery due — when the car must leave the workshop"
                >
                  <CalendarClock className="h-2 w-2" />
                  DD {deliveryDueLabel}
                </span>
              )}
            </div>
            {card.assigned_mechanic_name ? (
              <span className="text-xs font-medium text-green-700">{card.assigned_mechanic_name}</span>
            ) : (
              <span className="text-xs text-slate-400">Unassigned</span>
            )}
          </div>
          {canManage && (
            <div onClick={stopPropagation}>
              <JobCardMenu 
                card={card}
                user={user}
                mechanics={mechanics}
                onFetchMechanics={onFetchMechanics}
                onUpdateDeliveryDue={onUpdateDeliveryDue}
                onAssignMechanic={onAssignMechanic}
                onDelete={onDelete}
                onCreateInvoice={onCreateInvoice}
              />
            </div>
          )}
        </div>
        
        <div className="space-y-1 text-sm">
          {(card.vehicle_make || card.vehicle_model) && (
            <div className="flex items-center gap-2 text-slate-500 text-xs">
              <Car className="h-3 w-3 text-slate-400" />
              <span>{[card.vehicle_make, card.vehicle_model].filter(Boolean).join(' ')}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <User className="h-3 w-3 text-slate-400" />
            <span className="text-slate-700 truncate">{card.customer_name || 'Unknown'}</span>
          </div>
          {card.booked_datetime && (
            <div className="flex items-center gap-2 text-slate-500 text-xs" data-testid={`jc-booked-date-${card.job_card_id}`}>
              <Clock className="h-3 w-3 text-slate-400" />
              <span>{formatBookedDate(card.booked_datetime)}</span>
            </div>
          )}
          {card.loan_car_requested && (
            <div
              className="inline-flex items-center gap-1 text-[11px] font-medium text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-1.5 py-0.5 w-fit"
              data-testid={`jc-loan-car-pill-${card.job_card_id}`}
              title="Customer requested a loan car"
            >
              <Key className="h-3 w-3" />
              Loan car
            </div>
          )}
          {card.invoice_id && INVOICE_LINK_ROLES.includes(user?.role) && (
            <div className="flex items-center gap-1" onClick={stopPropagation}>
              <a
                href={withSessionHash(`/invoices/${card.invoice_id}`)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={stopPropagation}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5 hover:bg-emerald-100 w-fit"
                data-testid={`jc-invoice-created-${card.job_card_id}`}
                title="Open linked invoice"
              >
                <FileCheck className="h-3 w-3" />
                Invoice
              </a>
              <button
                type="button"
                onClick={(e) => openInvoicePdf(e, card.invoice_id)}
                className="inline-flex items-center text-[11px] font-bold text-white bg-emerald-600 border border-emerald-700 rounded-full px-1.5 py-0.5 hover:bg-emerald-700"
                data-testid={`jc-invoice-pdf-${card.job_card_id}`}
                title="Open the invoice PDF"
              >
                PDF
              </button>
            </div>
          )}
          {/* Dedicated productivity row — labour pill + over/under signal. */}
          <div className="pt-1 border-t border-slate-100" data-testid={`jc-productivity-row-${card.job_card_id}`}>
            <JobCardLabourLive card={card} compact />
            <JobCardPerMechanicHours card={card} mechanics={mechanics} />
          </div>
        </div>
      </Card>
    );
  }
  
  if (size === 'compact') {
    return (
      <Card 
        className={`p-2 bg-white border shadow-sm cursor-pointer hover:bg-slate-50 ${isDragging ? 'shadow-lg ring-2 ring-blue-400' : ''}`}
        data-testid={`job-card-${card.job_card_id}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
              card.priority === 'urgent' ? 'bg-red-500' :
              card.priority === 'high' ? 'bg-orange-500' : 'bg-slate-300'
            }`} />
            <RankBadge card={card} size="sm" />
            <span className="text-sm font-bold text-slate-900 tracking-wide">{formatRego(card.vehicle_rego) || 'NO REGO'}</span>
            <ReadyToQuotePill card={card} />
            <PartsPill card={card} canManage={canManage} onUpdate={onUpdate} />
            {hasChecklist && <ClipboardCheck className="h-3 w-3 text-green-500 flex-shrink-0" />}
            <OnBreakPill card={card} />
          </div>
          {card.assigned_mechanic_name ? (
            <span className="text-xs text-green-700 flex-shrink-0 ml-2">{card.assigned_mechanic_name}</span>
          ) : (
            <span className="text-xs text-slate-400 flex-shrink-0 ml-2">Unassigned</span>
          )}
        </div>
      </Card>
    );
  }
  
  // Mini view for yearly - show rego as primary
  return (
    <div 
      className={`flex items-center gap-1 p-1 bg-white rounded border text-xs cursor-pointer hover:bg-slate-50 ${isDragging ? 'ring-2 ring-blue-400' : ''}`}
      data-testid={`job-card-${card.job_card_id}`}
    >
      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
        card.priority === 'urgent' ? 'bg-red-500' :
        card.priority === 'high' ? 'bg-orange-500' : 'bg-slate-300'
      }`} />
      <span className="truncate font-medium">{formatRego(card.vehicle_rego) || 'NO REGO'}</span>
    </div>
  );
}

/* iter197 — Status-view tile perf. Mirrors the Wallet-view JCTile pattern
   (MechanicWalletBoard.jsx): React.memo + a hardcoded field whitelist so the
   tile only re-renders when a VISIBLE field actually changes. Previously this
   tile re-rendered for EVERY card on every board re-render (each drag update
   and each 30s poll) → sluggish drag-drop on big boards. Function props
   (onAssignMechanic, onCreateInvoice, …) are intentionally NOT compared — they
   are recreated each parent render but their behaviour doesn't depend on
   fast-changing captured state (same trade-off the Wallet tile makes).

   ⚠️ If you render a NEW `card.<field>` inside JobCardItemInner, add that field
   name to `_JCI_CARD_FIELDS` below or the tile will show stale data. */
const _JCI_CARD_FIELDS = [
  'job_card_id', 'status', 'vehicle_rego', 'vehicle_make', 'vehicle_model',
  'customer_name', 'customer_phone', 'assigned_mechanic_name', 'job_card_number',
  'priority', 'priority_rank', 'scheduled_date', 'booked_datetime', 'delivery_due_at',
  'parts_ordered', 'parts_ordered_at', 'ready_to_quote', 'invoice_id', 'on_break',
  'service_type', 'escalated', 'loan_car_requested', 'kanban_anchor_date', 'deleted_at',
];
function jcItemEqual(prev, next) {
  if (prev.isDragging !== next.isDragging) return false;
  if (prev.size !== next.size) return false;
  if (prev.canManage !== next.canManage) return false;
  if (prev.duplicateFlags !== next.duplicateFlags) return false;
  // Stable references (parent state/props) — safe to compare by identity.
  if (prev.mechanics !== next.mechanics) return false;
  if (prev.user !== next.user) return false;
  const a = prev.card || {}; const b = next.card || {};
  for (const k of _JCI_CARD_FIELDS) {
    if (a[k] !== b[k]) return false;
  }
  // line_items / quality_checklist changes flip the hours + checklist badges.
  const aLi = a.line_items || []; const bLi = b.line_items || [];
  if (aLi.length !== bLi.length) return false;
  if (a.quality_checklist !== b.quality_checklist) return false;
  return true;
}

export default memo(JobCardItemInner, jcItemEqual);

